import { FC, useEffect, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-grid-system';
import styled from 'styled-components';
import { Button } from '../atoms';
import { OwnerListTable, OwnerFilters, initialOwnerFilters } from '../organism';
import { useNavigate } from 'react-router';
import { pathURL } from '@/components/constants/urls';
import { useUserLanguage, useListNavigationStateContext } from '@/customHooks';
import { OwnerFilterInput, useGetOwnersQuery } from '@/graphql/generated';
import { Pagination } from '../molecules';

const textsData = {
  en: {
    title: 'Owners',
    buttonText: '+ Add Owner'
  },
  es: {
    title: 'Propietarios',
    buttonText: '+ Agregar Propietario'
  }
}

const ButtonContainer = styled(Col)`
  display: flex;
  align-items: center;
`

export const OwnerListPage: FC = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const navigate = useNavigate()
  const { getListState, setListState } = useListNavigationStateContext()
  const hasRestoredState = useRef(false)

  useEffect(() => {
    if (!hasRestoredState.current) {
      const savedState = getListState('owners')
      if (savedState) {
        setCurrentPage(savedState.currentPage)
        setFilters(savedState.filters as OwnerFilterInput)
      }
      hasRestoredState.current = true
    }
  }, [getListState])

  const [currentPage, setCurrentPage] = useState(() => {
    const savedState = getListState('owners')
    return savedState?.currentPage ?? 1
  });
  const [filters, setFilters] = useState<OwnerFilterInput>(() => {
    const savedState = getListState('owners')
    return (savedState?.filters as OwnerFilterInput) ?? initialOwnerFilters
  });

  useEffect(() => {
    if (hasRestoredState.current) {
      setListState('owners', {
        currentPage,
        filters,
      })
    }
  }, [currentPage, filters, setListState])

  const handleFilterChange = (newFilters: OwnerFilterInput) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to page 1 when filters change
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setFilters({ ...filters, page: page.toString() });
  };

  const { data } = useGetOwnersQuery({
    variables: {
      filterInput: { ...filters, page: currentPage.toString() },
    },
    fetchPolicy: 'network-only',
  });

  const currentDataCount = data?.owners?.items?.length || 0;
  const totalPages = data?.owners?.pagination?.totalPages || 1;
  const showPagination = currentDataCount > 0;

  return (
    <Container fluid>
      <Row>
        <Col>
            <h2>{texts.title}</h2>
        </Col>
        <ButtonContainer xs="content">
          <Button
            onClick={() => navigate(`/${pathURL.USER}/${pathURL.OWNER}/${pathURL.CREATE}`)}
          >
            {texts.buttonText}
          </Button>
        </ButtonContainer>
      </Row>
      <Row>
        <Col>
          <OwnerFilters filters={filters} onFilterChange={handleFilterChange} />
        </Col>
      </Row>
      <Row>
        <Col>
          <OwnerListTable filters={{ ...filters, page: currentPage.toString() }} />
        </Col>
      </Row>
      {showPagination && (
        <Row>
          <Col>
            <Pagination 
              currentPage={currentPage} 
              onPageChange={handlePageChange} 
              currentDataCount={currentDataCount}
              pageSize={filters.pageSize || null}
              totalPages={totalPages}
            />
          </Col>
        </Row>
      )}
    </Container>
  )
}